/* eslint-disable react-hooks/set-state-in-effect, react-hooks/preserve-manual-memoization */
import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, CalendarDays, Lock, Check, X } from 'lucide-react';
import {
  format, addMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay, isBefore, startOfToday,
} from 'date-fns';
import { it } from 'date-fns/locale';
import logger from '@/lib/logger';
import { AvailabilityChat } from '@/components/AvailabilityChat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const GRID_START_MIN = 7 * 60;   // 07:00
const GRID_END_MIN = 21 * 60;    // 21:00

/**
 * Calendario disponibilità riusabile (vista mensile + spunte a intervalli
 * pari alla durata slot configurata sul medico).
 * - doctorId assente => modalità dottore (usa il token).
 * - doctorId presente => modalità staff (admin/operatore) per quel dottore.
 */
export function AvailabilityCalendar({ doctorId }) {
  const [month, setMonth] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [slotDuration, setSlotDuration] = useState(30);

  const [selectedDay, setSelectedDay] = useState(null);
  const [anchorTime, setAnchorTime] = useState(null);

  // Durata slot del medico (campo "durata_slot_minuti" della scheda medico)
  useEffect(() => {
    const url = doctorId ? `${API}/doctors/${doctorId}` : `${API}/doctor/me`;
    axios.get(url)
      .then(r => setSlotDuration(r.data?.durata_slot_minuti || 30))
      .catch(() => setSlotDuration(30));
  }, [doctorId]);

  // Griglia orari dinamica in base alla durata slot del medico
  const gridTimes = useMemo(() => {
    const arr = [];
    for (let m = GRID_START_MIN; m + slotDuration <= GRID_END_MIN; m += slotDuration) {
      arr.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
    }
    return arr;
  }, [slotDuration]);

  const monthStart = startOfMonth(month);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const today = startOfToday();

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    try {
      const params = { data_from: format(gridStart, 'yyyy-MM-dd'), data_to: format(gridEnd, 'yyyy-MM-dd') };
      if (doctorId) params.doctor_id = doctorId;
      const res = await axios.get(`${API}/availability-slots`, { params });
      setSlots(res.data);
    } catch (err) {
      logger.error('Slots error:', err);
      toast.error('Errore nel caricamento della disponibilità');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format(gridStart, 'yyyy-MM-dd'), format(gridEnd, 'yyyy-MM-dd'), doctorId]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  const slotsByDay = (day) => slots.filter(s => s.data === format(day, 'yyyy-MM-dd'));

  const openDay = (day) => {
    if (isBefore(day, today)) return;
    setAnchorTime(null);
    setSelectedDay(day);
  };

  // Create availability slots for a list of times at once (Shift range selection)
  const createRange = async (times) => {
    if (!selectedDay || busy || times.length === 0) return;
    const dayStr = format(selectedDay, 'yyyy-MM-dd');
    setBusy(true);
    try {
      const body = { data: dayStr, orari: times, durata_minuti: slotDuration };
      if (doctorId) body.doctor_id = doctorId;
      const res = await axios.post(`${API}/availability-slots/batch`, body);
      await fetchSlots();
      if (res.data?.count > 0) toast.success(`${res.data.count} orari aggiunti`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operazione non riuscita');
    } finally {
      setBusy(false);
    }
  };

  // Toggle a single half-hour slot for the selected day
  const toggleSingle = async (time) => {
    if (!selectedDay || busy) return;
    const dayStr = format(selectedDay, 'yyyy-MM-dd');
    const existing = slots.find(s => s.data === dayStr && s.ora_inizio === time);
    setBusy(true);
    try {
      if (existing) {
        if (existing.status === 'booked') { toast.error('Slot prenotato: non rimovibile'); return; }
        await axios.delete(`${API}/availability-slots/${existing.id}`);
      } else {
        const body = { data: dayStr, ora_inizio: time, durata_minuti: slotDuration };
        if (doctorId) body.doctor_id = doctorId;
        await axios.post(`${API}/availability-slots`, body);
      }
      await fetchSlots();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operazione non riuscita');
    } finally {
      setBusy(false);
    }
  };

  // Handle click on a time cell (supports Shift+click range selection)
  const handleTimeClick = (time, shiftKey) => {
    if (busy) return;
    if (shiftKey && anchorTime) {
      const ai = gridTimes.indexOf(anchorTime);
      const ci = gridTimes.indexOf(time);
      if (ai === -1 || ci === -1) { toggleSingle(time); setAnchorTime(time); return; }
      const [lo, hi] = ai <= ci ? [ai, ci] : [ci, ai];
      const dayStr = format(selectedDay, 'yyyy-MM-dd');
      const rangeTimes = gridTimes.slice(lo, hi + 1).filter(t => {
        if (isTimePast(t)) return false;
        const ex = slots.find(s => s.data === dayStr && s.ora_inizio === t);
        return !ex; // only create the ones not yet present
      });
      if (rangeTimes.length === 0) { toast.info('Intervallo già disponibile'); return; }
      createRange(rangeTimes);
      setAnchorTime(time);
    } else {
      toggleSingle(time);
      setAnchorTime(time);
    }
  };

  const daySlots = selectedDay
    ? slots.filter(s => s.data === format(selectedDay, 'yyyy-MM-dd'))
    : [];
  const slotAtTime = (time) => daySlots.find(s => s.ora_inizio === time);
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const isTimePast = (time) => selectedDay && isSameDay(selectedDay, new Date())
    && (parseInt(time.split(':')[0], 10) * 60 + parseInt(time.split(':')[1], 10)) <= nowMin;

  const selectedDayAllSlots = selectedDay ? slotsByDay(selectedDay) : [];

  return (
    <div>
      {/* Assistente AI: definizione disponibilità in linguaggio naturale */}
      <AvailabilityChat doctorId={doctorId} onApplied={fetchSlots} />

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => setMonth(prev => addMonths(prev, -1))} data-testid="month-prev"><ChevronLeft size={16} /></Button>
            <CardTitle className="text-base flex items-center gap-2 capitalize" style={{ fontFamily: 'Space Grotesk' }}>
              <CalendarDays size={16} className="text-[#238A92]" />
              {format(month, 'MMMM yyyy', { locale: it })}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setMonth(new Date())}>Oggi</Button>
              <Button variant="ghost" size="sm" onClick={() => setMonth(prev => addMonths(prev, 1))} data-testid="month-next"><ChevronRight size={16} /></Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Clicca su un giorno per impostare gli orari di disponibilità (slot da {slotDuration} min). La disponibilità è indipendente dallo studio.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAYS.map(d => <div key={d} className="text-center text-xs font-medium text-muted-foreground py-1">{d}</div>)}
          </div>
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Caricamento…</div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {days.map(day => {
                const inMonth = isSameMonth(day, month);
                const past = isBefore(day, today);
                const isToday = isSameDay(day, new Date());
                const dSlots = slotsByDay(day);
                const bookedCount = dSlots.filter(s => s.status === 'booked').length;
                const availCount = dSlots.length - bookedCount;
                return (
                  <button
                    key={day.toISOString()}
                    disabled={past}
                    onClick={() => openDay(day)}
                    data-testid={`day-${format(day, 'yyyy-MM-dd')}`}
                    className={`min-h-[68px] rounded-lg border p-1.5 text-left transition-colors duration-150 flex flex-col
                      ${past ? 'bg-[hsl(var(--muted))]/40 text-muted-foreground/50 cursor-not-allowed' : 'hover:border-[#238A92] hover:bg-[#238A92]/5 cursor-pointer'}
                      ${!inMonth ? 'opacity-40' : ''}
                      ${isToday ? 'border-[#238A92] ring-1 ring-[#238A92]/30' : 'border-[hsl(var(--border))]'}`}
                  >
                    <span className={`text-xs font-semibold ${isToday ? 'text-[#238A92]' : ''}`}>{format(day, 'd')}</span>
                    <div className="mt-auto flex flex-wrap gap-1">
                      {availCount > 0 && <Badge className="bg-[#238A92]/15 text-[#0F5E66] text-[10px] px-1.5 py-0 h-4">{availCount} disp.</Badge>}
                      {bookedCount > 0 && <Badge className="bg-blue-100 text-blue-800 text-[10px] px-1.5 py-0 h-4">{bookedCount} pren.</Badge>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Day editor dialog */}
      <Dialog open={!!selectedDay} onOpenChange={(o) => !o && setSelectedDay(null)}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="capitalize" style={{ fontFamily: 'Space Grotesk' }}>
              {selectedDay && format(selectedDay, 'EEEE d MMMM yyyy', { locale: it })}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {selectedDayAllSlots.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Totale orari di disponibilità del giorno: {selectedDayAllSlots.length}.
              </p>
            )}

            <div>
              <p className="text-sm font-medium mb-1">Seleziona gli orari di disponibilità ({slotDuration} min)</p>
              <p className="text-xs text-muted-foreground mb-2">
                Clicca per attivare/disattivare un orario. Tieni premuto <kbd className="px-1.5 py-0.5 rounded border bg-[hsl(var(--muted))] text-[10px] font-medium">Shift</kbd> e clicca per selezionare un intervallo da un orario all'altro.
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {gridTimes.map(time => {
                  const slot = slotAtTime(time);
                  const checked = !!slot;
                  const booked = slot?.status === 'booked';
                  const past = isTimePast(time);
                  const disabled = busy || booked || past;
                  return (
                    <button
                      key={time}
                      type="button"
                      disabled={disabled}
                      onClick={(e) => handleTimeClick(time, e.shiftKey)}
                      data-testid={`halfhour-${time}`}
                      className={`flex items-center justify-between gap-1 px-2.5 py-2 rounded-lg border text-sm transition-colors duration-150
                        ${checked
                          ? (booked ? 'bg-blue-50 border-blue-400 text-blue-800' : 'bg-[#238A92] border-[#238A92] text-white')
                          : 'border-[hsl(var(--border))] text-[#1F2A37] hover:border-[#238A92]'}
                        ${anchorTime === time && !checked ? 'ring-2 ring-[#238A92]/40' : ''}
                        ${disabled && !booked ? 'opacity-50 cursor-not-allowed' : ''}
                        ${booked ? 'cursor-not-allowed' : ''}`}
                    >
                      <span className="font-medium">{time}</span>
                      {booked ? <Lock size={13} /> : checked ? <Check size={14} /> : null}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-[#238A92]"></span> Disponibile</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-blue-400"></span> Prenotato (bloccato)</span>
              </div>

              {/* Slot esistenti non allineati alla griglia attuale (es. creati con durata diversa) */}
              {daySlots.filter(s => !gridTimes.includes(s.ora_inizio)).length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-medium mb-1.5">Altri slot esistenti (creati con durata diversa)</p>
                  <div className="flex flex-wrap gap-1.5">
                    {daySlots.filter(s => !gridTimes.includes(s.ora_inizio)).map(s => (
                      <button
                        key={s.id}
                        type="button"
                        disabled={busy || s.status === 'booked'}
                        onClick={() => toggleSingle(s.ora_inizio)}
                        data-testid={`offgrid-slot-${s.ora_inizio}`}
                        className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs transition-colors duration-150 ${
                          s.status === 'booked'
                            ? 'bg-blue-50 border-blue-400 text-blue-800 cursor-not-allowed'
                            : 'bg-[#238A92]/10 border-[#238A92] text-[#0F5E66] hover:bg-red-50 hover:border-red-400 hover:text-red-700'
                        }`}
                        title={s.status === 'booked' ? 'Prenotato: non rimovibile' : 'Clicca per rimuovere'}
                      >
                        {s.ora_inizio} ({s.durata_minuti} min)
                        {s.status === 'booked' ? <Lock size={11} /> : <X size={11} />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setSelectedDay(null)} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="day-close">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AvailabilityCalendar;
